import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Container,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowBack, Download } from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, NavLink as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import { getEmployee } from "../processSlices";
import { getCompany } from "../../setups/setupSlices";
import { Company } from "../../setups/types/setupTypes";
import { Employee } from "../types/processTypes";
import { CvTemplateRendererOrEmpty } from "./components/CvTemplateRenderer";
import { cvTemplateToLabel } from "./utils/cvTemplateMappers";
import { useBulkEmployeePdfDownload } from "./hooks/useBulkEmployeePdfDownload";
import AuthSkeleton from "../../auth/AuthSkeleton";

type BulkCvLocationState = {
  employeeIds?: string[];
  officeId?: string;
};

function PremiumTemplateBulkEmployees() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as BulkCvLocationState;

  const employeeIds = state.employeeIds ?? [];
  const officeId = state.officeId ?? "";

  const [office, setOffice] = useState<Company>();
  const [renderEmployee, setRenderEmployee] = useState<Employee>();
  const [renderNonce, setRenderNonce] = useState(0);
  const [loadingOffice, setLoadingOffice] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();

  const previewRootRef = useRef<HTMLDivElement>(null);
  const readyResolverRef = useRef<(() => void) | null>(null);
  const { downloadBulk, isDownloading, progress } =
    useBulkEmployeePdfDownload(previewRootRef);

  useEffect(() => {
    if (!officeId || employeeIds.length === 0) {
      setLoadingOffice(false);
      return;
    }

    let cancelled = false;
    setLoadingOffice(true);
    void dispatch(getCompany(officeId))
      .unwrap()
      .then((full: Company) => {
        if (!cancelled) setOffice(full);
      })
      .catch(() => {
        if (!cancelled) setErrorMessage("Failed to load office");
      })
      .finally(() => {
        if (!cancelled) setLoadingOffice(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, officeId, employeeIds.length]);

  useEffect(() => {
    if (!renderNonce || !renderEmployee || !readyResolverRef.current) return;

    const resolve = readyResolverRef.current;
    readyResolverRef.current = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  }, [renderEmployee, renderNonce]);

  const prepareEmployee = useCallback(
    async (employeeId: string): Promise<Employee | null> => {
      const result = await dispatch(getEmployee(employeeId));
      if (!getEmployee.fulfilled.match(result)) return null;
      const employee = result.payload as Employee;
      if (!employee.givenName) return null;

      await new Promise<void>((resolve) => {
        readyResolverRef.current = resolve;
        setRenderEmployee(employee);
        setRenderNonce((n) => n + 1);
      });

      return employee;
    },
    [dispatch],
  );

  const handleDownload = async () => {
    if (!office?.cvTemplate || employeeIds.length === 0) return;
    setErrorMessage(undefined);
    try {
      await downloadBulk(employeeIds, office, prepareEmployee);
    } catch {
      setErrorMessage("Download failed");
    }
  };

  if (!officeId || employeeIds.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ minHeight: "100%", py: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("no_candidates_found") || "No candidates selected"}
          </Typography>
          <Button
            color="secondary"
            variant="contained"
            component={RouterLink}
            to="/app/cvs"
            startIcon={<ArrowBack />}
          >
            {t("back") || "Back"}
          </Button>
        </Box>
      </Container>
    );
  }

  if (loadingOffice) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ minHeight: "100%", py: 3 }}>
          <AuthSkeleton />
        </Box>
      </Container>
    );
  }

  if (!office?.cvTemplate) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ minHeight: "100%", py: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {errorMessage || "No Template Found"}
          </Typography>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBack />}
          >
            {t("back") || "Back"}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ minHeight: "100%", py: 3 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              color="secondary"
              variant="contained"
              onClick={() => navigate(-1)}
              disabled={isDownloading}
              startIcon={<ArrowBack />}
            >
              {t("back") || "Back"}
            </Button>
            <Typography variant="h6">
              {office.displayName} — {employeeIds.length}{" "}
              {t("candidates") || "candidates"}
            </Typography>
          </Stack>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => void handleDownload()}
            disabled={isDownloading}
            startIcon={<Download />}
          >
            {isDownloading
              ? `${progress.current} / ${progress.total}`
              : t("download") || "Download CVs"}
          </Button>
        </Stack>

        {isDownloading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={
                progress.total > 0
                  ? (progress.current / progress.total) * 100
                  : 0
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {progress.current} / {progress.total}
            </Typography>
          </Box>
        )}

        {errorMessage && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}

        <Box
          component={Paper}
          elevation={3}
          ref={previewRootRef}
          sx={{
            display: "block",
            overflow: "hidden",
            width: "100%",
            mt: 1,
          }}
          id="printCV"
        >
          <div dir="ltr">
            {renderEmployee?.givenName ? (
              <CvTemplateRendererOrEmpty
                employee={renderEmployee}
                company={office}
                cvTemplate={cvTemplateToLabel(office.cvTemplate)}
                backgroundColor={office.cvTemplate.bgColor ?? "#ffffff"}
              />
            ) : (
              <Typography variant="body1" sx={{ p: 2 }}>
                {t("download") || "Download"} to generate CVs
              </Typography>
            )}
          </div>
        </Box>
      </Box>
    </Container>
  );
}

export default PremiumTemplateBulkEmployees;
